import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  // FormControlLabel,
  // Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../store/system.store';
import { AgentService, CreateSystemUserRequest } from '../../services/agent.service';

interface CreateSystemUserData {
  email: string;
  fullName: string;
  isClient: boolean;
  password: string;
}

const CreateSystemUserPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState<CreateSystemUserData>({
    email: '',
    fullName: '',
    isClient: true,
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<CreateSystemUserData>>({});
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateSystemUserData> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccess(false);

    try {
      const payload: CreateSystemUserRequest = {
        email: formData.email,
        fullName: formData.fullName,
        isClient: formData.isClient,
        password: formData.password || 'Aa123456!',
      };

      const response = await AgentService.createSystemUser(payload);

      if (response.success) {
        setSuccess(true);
        setFormData({ email: '', fullName: '', isClient: false, password: '' });
        
        dispatch(
          showSnackbar({
            isError: false,
            msg: response.message || 'System user created successfully!',
          })
        );
      } else {
        throw new Error(response.message || 'Failed to create system user');
      }
    } catch (error) {
      console.error('Error creating system user:', error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: error instanceof Error ? error.message : 'Failed to create system user. Please try again.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateSystemUserData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isClient' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box 
        sx={{ 
          padding: { xs: "16px", md: "24px" }, 
          backgroundColor: theme.palette.grey[50], 
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton 
            onClick={() => navigate('/admin')} 
            sx={{ 
              p: 1,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: theme.palette.grey[100] }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
            Create System User
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 600 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
              System User Registration
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                System user created successfully! The user can now log in with the provided password.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  InputProps={{
                    startAdornment: (
                      <Email color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.fullName}
                  onChange={handleInputChange('fullName')}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  required
                  InputProps={{
                    startAdornment: (
                      <Person color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  error={!!errors.password}
                  helperText={errors.password}
                  required
                  InputProps={{
                    startAdornment: (
                      <Lock color="action" sx={{ mr: 1 }} />
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />

                {/* <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isClient}
                      onChange={handleInputChange('isClient')}
                      color="primary"
                    />
                  }
                  label="Is Client User"
                />

                <Alert severity="info">
                  If "Is Client User" is enabled, the user will have access to client-specific features. 
                  Otherwise, they will have standard system access.
                </Alert> */}

                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={isLoading || !formData.email || !formData.fullName || !formData.password}
                  startIcon={<Person />}
                  sx={{ textTransform: 'none' }}
                >
                  {isLoading ? 'Creating User...' : 'Create System User'}
                </Button>
              </Stack>
            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateSystemUserPage;